import { useState, useEffect, useCallback, useRef } from 'react';
import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { attachClosestEdge, extractClosestEdge, type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../stores/builderStore';
import type { LiveQuizSettingsConfig } from '../../types/form';
import { generateUUID } from '../../utils/uuid';

// Utility function to move item in array
function arrayMove<T>(array: T[], from: number, to: number): T[] {
  const newArray = [...array];
  const [removed] = newArray.splice(from, 1);
  newArray.splice(to, 0, removed);
  return newArray;
}

interface LiveQuizBuilderProps {
  formId?: string;
}

interface QuizQuestion {
  id: string;
  question: string;
  options: { id: string; text: string; isCorrect: boolean }[];
  timeLimit: number;
  points: number;
  imageUrl?: string;
}

const DEFAULT_LIVE_SETTINGS: LiveQuizSettingsConfig = {
  questionTimeSeconds: 20,
  showQuestionOnPlayerDevice: true,
  enableStreakBonus: true,
  streakBonusMultiplier: 1.2,
  streakThreshold: 3,
  timeBasedScoring: true,
  maxPointsPerQuestion: 1000,
  minPointsPerQuestion: 500,
  showLeaderboardAfterQuestion: true,
  leaderboardSize: 5,
  showAnswerDistribution: true,
  showCorrectAnswer: true,
  allowLateJoin: true,
  autoAdvance: false,
  autoAdvanceDelaySeconds: 5,
  randomizeQuestions: false,
  randomizeOptions: false,
  enableSounds: true,
};

function SortableQuestion({
  question,
  index,
  isSelected,
  onSelect,
  onDelete,
  t
}: {
  question: QuizQuestion;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  t: any;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);

  // Add draggable + droppable for reordering
  useEffect(() => {
    const element = ref.current;
    const dragHandle = dragHandleRef.current;
    if (!element || !dragHandle) return;

    return combine(
      draggable({
        element: dragHandle,
        getInitialData: () => ({
          type: 'quiz-question',
          questionId: question.id,
          index,
        }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        getData: ({ input, element }) => {
          return attachClosestEdge(
            {
              type: 'quiz-question-reorder',
              questionId: question.id,
              index,
            },
            {
              input,
              element,
              allowedEdges: ['top', 'bottom'],
            }
          );
        },
        onDragEnter: ({ self }) => {
          const edge = extractClosestEdge(self.data);
          setClosestEdge(edge);
        },
        onDragLeave: () => {
          setClosestEdge(null);
        },
        onDrop: () => {
          setClosestEdge(null);
        },
      })
    );
  }, [question.id, index]);

  const correctCount = question.options.filter(o => o.isCorrect).length;

  return (
    <div className="relative">
      {/* Drop indicator at top */}
      {closestEdge === 'top' && <DropIndicator edge="top" gap="8px" />}

      <div
        ref={ref}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        onClick={onSelect}
        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
          isSelected
            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            ref={dragHandleRef}
            className="mt-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm0 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm-2 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm8-14a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm-2 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm2 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('builder.questionNumber', { number: index + 1 })}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                {t('builder.timeSeconds', { time: question.timeLimit })}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                {question.points} {t('builder.pts')}
              </span>
            </div>
            <p className="text-gray-900 dark:text-white font-medium truncate">
            {question.question || t('builder.untitledQuestion')}
          </p>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{question.options.length} {t('builder.options')}</span>
            <span>•</span>
            <span className={correctCount === 0 ? 'text-red-500' : 'text-green-500'}>
              {correctCount} {t('builder.correct')}
            </span>
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>

    {/* Drop indicator at bottom */}
    {closestEdge === 'bottom' && <DropIndicator edge="bottom" gap="8px" />}
  </div>
  );
}

function QuestionEditor({
  question,
  onChange,
  t
}: {
  question: QuizQuestion;
  onChange: (updates: Partial<QuizQuestion>) => void;
  t: any;
}) {
  const addOption = () => {
    if (question.options.length >= 6) return;
    const newOption = {
      id: generateUUID(),
      text: '',
      isCorrect: false,
    };
    onChange({ options: [...question.options, newOption] });
  };

  const updateOption = (optionId: string, updates: Partial<{ text: string; isCorrect: boolean }>) => {
    onChange({
      options: question.options.map(opt =>
        opt.id === optionId ? { ...opt, ...updates } : opt
      ),
    });
  };

  const removeOption = (optionId: string) => {
    if (question.options.length <= 2) return;
    onChange({ options: question.options.filter(opt => opt.id !== optionId) });
  };

  const optionColors = [
    'bg-red-500',
    'bg-blue-500',
    'bg-yellow-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
  ];

  return (
    <div className="space-y-6">
      {/* Question Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('builder.question')}
        </label>
        <textarea
          value={question.question}
          onChange={(e) => onChange({ question: e.target.value })}
          placeholder={t('builder.questionPlaceholder')}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
          rows={3}
        />
      </div>

      {/* Image URL (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('builder.imageUrl')}
        </label>
        <input
          type="url"
          value={question.imageUrl || ''}
          onChange={(e) => onChange({ imageUrl: e.target.value || undefined })}
          placeholder={t('builder.imageUrlPlaceholder')}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        />
        {question.imageUrl && (
          <div className="mt-2 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
            <img
              src={question.imageUrl}
              alt="Question"
              className="max-h-48 mx-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
      </div>

      {/* Answer Options */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('builder.answerOptions')}
          </label>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('builder.clickToMarkCorrect')}
          </span>
        </div>

        <div className="space-y-3">
          {question.options.map((option, idx) => (
            <div key={option.id} className="flex items-center gap-3">
              <div className={`w-10 h-10 ${optionColors[idx]} rounded-lg flex items-center justify-center text-white font-bold text-lg`}>
                {String.fromCharCode(65 + idx)}
              </div>
              <input
                type="text"
                value={option.text}
                onChange={(e) => updateOption(option.id, { text: e.target.value })}
                placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={() => updateOption(option.id, { isCorrect: !option.isCorrect })}
                className={`p-2.5 rounded-lg transition-colors ${
                  option.isCorrect
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-green-100 hover:text-green-600'
                }`}
                title={option.isCorrect ? 'Correct answer' : 'Mark as correct'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              {question.options.length > 2 && (
                <button
                  onClick={() => removeOption(option.id)}
                  className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>

        {question.options.length < 6 && (
          <button
            onClick={addOption}
            className="mt-3 flex items-center gap-2 px-4 py-2 text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('builder.addOption')}
          </button>
        )}
      </div>

      {/* Question Settings */}
      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('builder.timeLimit')}
          </label>
          <select
            value={question.timeLimit}
            onChange={(e) => onChange({ timeLimit: Number(e.target.value) })}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value={5}>{t('builder.secondsValue', { value: 5 })}</option>
            <option value={10}>{t('builder.secondsValue', { value: 10 })}</option>
            <option value={15}>{t('builder.secondsValue', { value: 15 })}</option>
            <option value={20}>{t('builder.secondsValue', { value: 20 })}</option>
            <option value={30}>{t('builder.secondsValue', { value: 30 })}</option>
            <option value={45}>{t('builder.secondsValue', { value: 45 })}</option>
            <option value={60}>{t('builder.secondsValue', { value: 60 })}</option>
            <option value={90}>{t('builder.secondsValue', { value: 90 })}</option>
            <option value={120}>2 {t('builder.timeLimit')}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('builder.points')}
          </label>
          <select
            value={question.points}
            onChange={(e) => onChange({ points: Number(e.target.value) })}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value={500}>{t('builder.pointsValue', { value: 500 })}</option>
            <option value={1000}>{t('builder.pointsValue', { value: 1000 })}</option>
            <option value={1500}>{t('builder.pointsValue', { value: 1500 })}</option>
            <option value={2000}>{t('builder.pointsValue', { value: 2000 })}</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function LiveQuizSettings({
  settings,
  onChange,
  t
}: {
  settings: LiveQuizSettingsConfig;
  onChange: (settings: LiveQuizSettingsConfig) => void;
  t: any;
}) {
  return (
    <div className="space-y-6">
      {/* Timing Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t('builder.settingsPanel.timing')}
        </h4>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('builder.settingsPanel.defaultQuestionTime')}</label>
            <select
              value={settings.questionTimeSeconds}
              onChange={(e) => onChange({ ...settings, questionTimeSeconds: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
            >
              <option value={10}>{t('builder.secondsValue', { value: 10 })}</option>
              <option value={15}>{t('builder.secondsValue', { value: 15 })}</option>
              <option value={20}>{t('builder.secondsValue', { value: 20 })}</option>
              <option value={30}>{t('builder.secondsValue', { value: 30 })}</option>
              <option value={45}>{t('builder.secondsValue', { value: 45 })}</option>
              <option value={60}>{t('builder.secondsValue', { value: 60 })}</option>
            </select>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.autoAdvance}
              onChange={(e) => onChange({ ...settings, autoAdvance: e.target.checked })}
              className="w-4 h-4 text-orange-500 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.autoAdvance')}</span>
          </label>
          {settings.autoAdvance && (
            <div className="ml-7">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('builder.settingsPanel.delayBeforeAdvance')}</label>
              <select
                value={settings.autoAdvanceDelaySeconds}
                onChange={(e) => onChange({ ...settings, autoAdvanceDelaySeconds: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
              >
                <option value={3}>{t('builder.secondsValue', { value: 3 })}</option>
                <option value={5}>{t('builder.secondsValue', { value: 5 })}</option>
                <option value={10}>{t('builder.secondsValue', { value: 10 })}</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Scoring Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          {t('builder.settingsPanel.scoring')}
        </h4>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.timeBasedScoring}
              onChange={(e) => onChange({ ...settings, timeBasedScoring: e.target.checked })}
              className="w-4 h-4 text-orange-500 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.timeBasedScoring')}</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.enableStreakBonus}
              onChange={(e) => onChange({ ...settings, enableStreakBonus: e.target.checked })}
              className="w-4 h-4 text-orange-500 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.streakBonus')}</span>
          </label>
          {settings.enableStreakBonus && (
            <div className="ml-7 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('builder.settingsPanel.streakThreshold')}</label>
                <select
                  value={settings.streakThreshold}
                  onChange={(e) => onChange({ ...settings, streakThreshold: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                >
                  <option value={2}>{t('builder.settingsPanel.inARow', { count: 2 })}</option>
                  <option value={3}>{t('builder.settingsPanel.inARow', { count: 3 })}</option>
                  <option value={5}>{t('builder.settingsPanel.inARow', { count: 5 })}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('builder.settingsPanel.bonusMultiplier')}</label>
                <select
                  value={settings.streakBonusMultiplier}
                  onChange={(e) => onChange({ ...settings, streakBonusMultiplier: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                >
                  <option value={1.1}>1.1x</option>
                  <option value={1.2}>1.2x</option>
                  <option value={1.5}>1.5x</option>
                  <option value={2}>2x</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Display Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          {t('builder.settingsPanel.display')}
        </h4>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showQuestionOnPlayerDevice}
              onChange={(e) => onChange({ ...settings, showQuestionOnPlayerDevice: e.target.checked })}
              className="w-4 h-4 text-orange-500 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.showQuestionOnDevices')}</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showLeaderboardAfterQuestion}
              onChange={(e) => onChange({ ...settings, showLeaderboardAfterQuestion: e.target.checked })}
              className="w-4 h-4 text-orange-500 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.showLeaderboardAfterQuestion')}</span>
          </label>
          {settings.showLeaderboardAfterQuestion && (
            <div className="ml-7">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('builder.settingsPanel.showTopPlayers')}</label>
              <select
                value={settings.leaderboardSize}
                onChange={(e) => onChange({ ...settings, leaderboardSize: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
              >
                <option value={3}>{t('builder.settingsPanel.topN', { count: 3 })}</option>
                <option value={5}>{t('builder.settingsPanel.topN', { count: 5 })}</option>
                <option value={10}>{t('builder.settingsPanel.topN', { count: 10 })}</option>
              </select>
            </div>
          )}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showAnswerDistribution}
              onChange={(e) => onChange({ ...settings, showAnswerDistribution: e.target.checked })}
              className="w-4 h-4 text-orange-500 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.showAnswerDistribution')}</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showCorrectAnswer}
              onChange={(e) => onChange({ ...settings, showCorrectAnswer: e.target.checked })}
              className="w-4 h-4 text-orange-500 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.revealCorrectAnswer')}</span>
          </label>
        </div>
      </div>

      {/* Gameplay Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t('builder.settingsPanel.gameplay')}
        </h4>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.allowLateJoin}
              onChange={(e) => onChange({ ...settings, allowLateJoin: e.target.checked })}
              className="w-4 h-4 text-orange-500 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.allowLateJoin')}</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.randomizeQuestions}
              onChange={(e) => onChange({ ...settings, randomizeQuestions: e.target.checked })}
              className="w-4 h-4 text-orange-500 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.randomizeQuestions')}</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.randomizeOptions}
              onChange={(e) => onChange({ ...settings, randomizeOptions: e.target.checked })}
              className="w-4 h-4 text-orange-500 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.randomizeOptions')}</span>
          </label>
        </div>
      </div>

      {/* Sound Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
          {t('builder.settingsPanel.soundEffects')}
        </h4>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.enableSounds}
            onChange={(e) => onChange({ ...settings, enableSounds: e.target.checked })}
            className="w-4 h-4 text-orange-500 rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">{t('builder.settingsPanel.enableSounds')}</span>
        </label>
      </div>
    </div>
  );
}

export default function LiveQuizBuilder({ formId: _formId }: LiveQuizBuilderProps) {
  const { t } = useTranslation('liveQuiz');
  const { formSchema, updateSettings, updateQuizQuestions } = useBuilderStore();
  // formId can be used for future features like auto-save
  void _formId;
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'questions' | 'settings'>('questions');
  const [liveSettings, setLiveSettings] = useState<LiveQuizSettingsConfig>(
    formSchema.settings.quiz?.liveSettings || DEFAULT_LIVE_SETTINGS
  );

  // Convert form fields to quiz questions on load
  useEffect(() => {
    const quizQuestions: QuizQuestion[] = formSchema.fields
      .filter(f => f.type === 'radio' || f.type === 'checkbox' || f.type === 'image_choice')
      .map(f => ({
        id: f.id,
        question: f.label,
        options: (f.options || []).map(opt => ({
          id: opt.value,
          text: opt.label,
          isCorrect: Array.isArray(f.properties.correctAnswer)
            ? f.properties.correctAnswer.includes(opt.value)
            : f.properties.correctAnswer === opt.value,
        })),
        timeLimit: f.properties.points ? 20 : 20, // Could store per-question time
        points: f.properties.points || 1000,
        imageUrl: f.type === 'image_choice' ? undefined : undefined,
      }));

    if (quizQuestions.length > 0) {
      setQuestions(quizQuestions);
      setSelectedQuestionId(quizQuestions[0].id);
    }
  }, []);

  // Sync settings changes to the store
  useEffect(() => {
    const quizSettings = formSchema.settings.quiz || { enabled: true, mode: 'live' as const };
    updateSettings({
      quiz: {
        ...quizSettings,
        enabled: true,
        mode: 'live',
        liveSettings,
      },
    });
  }, [liveSettings]);

  // Sync questions to form fields
  const syncQuestionsToFields = useCallback(() => {
    // Update fields in the store using the proper action
    // This ensures undo/redo works correctly
    updateQuizQuestions(questions);
  }, [questions, updateQuizQuestions]);

  // Sync whenever questions change
  useEffect(() => {
    if (questions.length > 0) {
      syncQuestionsToFields();
    }
  }, [questions, syncQuestionsToFields]);

  const addQuestion = () => {
    const newQuestion: QuizQuestion = {
      id: generateUUID(),
      question: '',
      options: [
        { id: generateUUID(), text: '', isCorrect: true },
        { id: generateUUID(), text: '', isCorrect: false },
        { id: generateUUID(), text: '', isCorrect: false },
        { id: generateUUID(), text: '', isCorrect: false },
      ],
      timeLimit: liveSettings.questionTimeSeconds,
      points: liveSettings.maxPointsPerQuestion,
    };
    setQuestions([...questions, newQuestion]);
    setSelectedQuestionId(newQuestion.id);
  };

  const updateQuestion = (questionId: string, updates: Partial<QuizQuestion>) => {
    setQuestions(questions.map(q =>
      q.id === questionId ? { ...q, ...updates } : q
    ));
  };

  const deleteQuestion = (questionId: string) => {
    const newQuestions = questions.filter(q => q.id !== questionId);
    setQuestions(newQuestions);
    if (selectedQuestionId === questionId) {
      setSelectedQuestionId(newQuestions[0]?.id || null);
    }
  };

  // Monitor for drop events to handle quiz question reordering
  useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        const dropTargets = location.current.dropTargets;
        if (dropTargets.length === 0) return;

        const target = dropTargets[0];
        const sourceData = source.data;
        const targetData = target.data;

        // Handle quiz question reordering
        if (sourceData.type === 'quiz-question' && targetData.type === 'quiz-question-reorder') {
          const edge = extractClosestEdge(targetData);
          const sourceIndex = sourceData.index as number;
          const targetIndex = targetData.index as number;

          if (sourceIndex !== targetIndex) {
            let newIndex = targetIndex;
            if (edge === 'bottom') {
              newIndex = targetIndex + 1;
            }

            // Adjust if moving down
            if (sourceIndex < targetIndex) {
              newIndex -= 1;
            }

            setQuestions(arrayMove(questions, sourceIndex, newIndex));
          }
        }
      },
    });
  }, [questions]);

  const selectedQuestion = questions.find(q => q.id === selectedQuestionId);

  // Calculate validation errors
  const validationErrors: string[] = [];
  if (questions.length === 0) {
    validationErrors.push('Add at least one question');
  }
  questions.forEach((q, idx) => {
    if (!q.question.trim()) {
      validationErrors.push(`Question ${idx + 1} has no text`);
    }
    if (q.options.filter(o => o.isCorrect).length === 0) {
      validationErrors.push(`Question ${idx + 1} has no correct answer`);
    }
    if (q.options.some(o => !o.text.trim())) {
      validationErrors.push(`Question ${idx + 1} has empty options`);
    }
  });

  return (
    <div className="flex h-[calc(100vh-8rem)] bg-gray-50 dark:bg-gray-900">
      {/* Left Sidebar - Question List */}
      <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('questions')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'questions'
                ? 'text-orange-600 border-b-2 border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {t('builder.questionsCount', { count: questions.length })}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'settings'
                ? 'text-orange-600 border-b-2 border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {t('builder.settings')}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'questions' ? (
            <div className="space-y-3">
              {questions.map((question, index) => (
                <SortableQuestion
                  key={question.id}
                  question={question}
                  index={index}
                  isSelected={selectedQuestionId === question.id}
                  onSelect={() => setSelectedQuestionId(question.id)}
                  onDelete={() => deleteQuestion(question.id)}
                  t={t}
                />
              ))}
            </div>
          ) : (
            <LiveQuizSettings settings={liveSettings} onChange={setLiveSettings} t={t} />
          )}
        </div>

        {/* Add Question Button */}
        {activeTab === 'questions' && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={addQuestion}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('builder.addQuestion')}
            </button>
          </div>
        )}
      </div>

      {/* Main Content - Question Editor */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedQuestion ? (
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg font-semibold">
                  {t('builder.questionNumber', { number: questions.findIndex(q => q.id === selectedQuestionId) + 1 })}
                </span>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('builder.editQuestion')}
                </h2>
              </div>
              <QuestionEditor
                question={selectedQuestion}
                onChange={(updates) => updateQuestion(selectedQuestionId!, updates)}
                t={t}
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('builder.noQuestionsYet')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {t('builder.noQuestionsDescription')}
              </p>
              <button
                onClick={addQuestion}
                className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('builder.addFirstQuestion')}
              </button>
            </div>
          </div>
        )}

        {/* Validation Errors Bar */}
        {validationErrors.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800 px-6 py-3">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  {t('builder.fixIssuesBeforePublishing')}
                </p>
                <ul className="mt-1 text-sm text-red-600 dark:text-red-400 list-disc list-inside">
                  {validationErrors.slice(0, 3).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {validationErrors.length > 3 && (
                    <li>{t('builder.andMoreIssues', { count: validationErrors.length - 3 })}</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
